import React, { useState, useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import * as Sentry from "@sentry/react";

const WORKBOOK_CAMERA_FILE = "components/WorkbookCamera.jsx";

function safeStringify(value, max = 5000) {
  try {
    const json = JSON.stringify(value, (key, val) => {
      if (typeof val === "string" && val.length > 2000) {
        return val.slice(0, 2000) + "…[truncated]";
      }
      return val;
    });
    return json.length > max ? json.slice(0, max) + "…[truncated]" : json;
  } catch {
    return "[Unserializable]";
  }
}

const captureSentryError = (heading, error, extra = {}) => {
  let normalizedError;

  if (error instanceof Error) {
    // Keep original message and stack, trim only if message is huge
    const shortMessage =
      error.message.length > 1000
        ? error.message.slice(0, 1000) + "…[truncated]"
        : error.message;

    normalizedError = new Error(shortMessage);
    normalizedError.name = error.name;
    normalizedError.stack = error.stack;
  } else {
    // Non-Error inputs → wrap into Error
    normalizedError = new Error(
      typeof error === "string" ? error.slice(0, 1000) : heading,
    );
  }

  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("component", "WorkbookCamera");
    scope.setExtra("error_heading", heading);
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    if (!(error instanceof Error)) {
      scope.setExtra("original_error_value", error);
    }
    Sentry.captureException(normalizedError);
  });
};

const WorkbookCamera = ({ isOpen, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);

  // Robustly attempt to play the video to avoid first-try failures
  const waitForMetadata = (video) =>
    new Promise((resolve) => {
      if (!video) return resolve();
      if (video.readyState >= 1) return resolve();
      const onLoaded = () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        resolve();
      };
      video.addEventListener("loadedmetadata", onLoaded);
    });

  const tryPlayVideo = async (video, attempts = 3) => {
    for (let i = 0; i < attempts; i++) {
      try {
        await waitForMetadata(video);
        const playResult = video.play();
        if (playResult && typeof playResult.then === "function") {
          await playResult;
        }
        return true;
      } catch (e) {
        // Small backoff before retrying
        await new Promise((r) => setTimeout(r, 150 * (i + 1)));
      }
    }
    return false;
  };

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 660 },
          height: { ideal: 480 },
        },
      });

      mediaStream.onended = () => {
        setError("Camera stream disconnected. Please try again.");
        setStream(null);
      };

      mediaStream.onerror = (error) => {
        captureSentryError(
          `${WORKBOOK_CAMERA_FILE} | startCamera::mediaStream.onerror | L82`,
          error,
          {
            facingMode: "environment",
            videoWidth: 660,
            videoHeight: 480,
          },
        );
        console.error("Media stream error:", error);
        setError("Camera stream error. Please try again.");
        setStream(null);
      };

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        const ok = await tryPlayVideo(videoRef.current, 3);
        if (!ok) {
          captureSentryError(
            `${WORKBOOK_CAMERA_FILE} | startCamera::initialPlayback | L100`,
            new Error("Video playback failed after retries"),
            {
              videoReadyState: videoRef.current?.readyState ?? null,
              streamActive: Boolean(mediaStream),
            },
          );
          console.error("Error playing video after retries");
          setError("Failed to display camera. Please try again.");
        } else {
          setError(null);
        }
      }
      setError(null);
    } catch (err) {
      captureSentryError(`${WORKBOOK_CAMERA_FILE} | startCamera | L117`, err, {
        constraints: { facingMode: "environment", width: 660, height: 480 },
      });
      console.error("Error accessing camera:", err);
      if (err.name === "NotAllowedError") {
        setError("Camera access denied. Please allow camera permissions.");
      } else if (err.name === "NotFoundError") {
        setError("Camera not found. Please check your device.");
      } else if (err.name === "NotReadableError") {
        setError("Camera is in use by another application.");
      } else {
        setError(`Camera error: ${err.message}`);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.setTransform(1, 0, 0, 1, 0, 0);

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          onCapture(file);
          onClose(); // Close the modal after capturing
        }
      },
      "image/jpeg",
      0.9,
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-800">Take a Photo</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-4">
          {error ? (
            <div className="text-red-500 text-sm mb-4 text-center">{error}</div>
          ) : (
            <div
              className="relative bg-black rounded-lg overflow-hidden mb-4"
              style={{ paddingTop: "75%" }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
                style={{ transform: "scaleX(-1)", transformOrigin: "center" }}
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={capturePhoto}
              disabled={!!error}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Capture
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkbookCamera;
